import { AuthLogout } from "../../../api/action/user.jsx";
import {
  KIOSK_ENTRY_PIN,
  KIOSK_PIN_VERIFIED_KEY,
  KIOSK_SESSION_FLAG_KEY,
  KIOSK_SESSION_STARTED_AT_KEY,
} from "./kioskConfig.js";

const getStorage = () => (typeof window !== "undefined" ? window.sessionStorage : null);
const getLocalStorage = () => (typeof window !== "undefined" ? window.localStorage : null);

export const isKioskSessionActive = () => (
  getStorage()?.getItem(KIOSK_SESSION_FLAG_KEY) === "true"
);

/**
 * Single source of truth for the kiosk *entry* PIN gate (distinct from
 * KIOSK_EXIT_PIN, checked separately in KioskHome.jsx's staff-exit prompt).
 * Both entry points — the Alt+K shortcut (App.jsx) and manually opening
 * /kiosk (KioskPinGate.jsx) — call this same function so the PIN value, the
 * comparison, and the "verified" side effect can never drift apart between
 * the two.
 */
export const isKioskPinVerified = () => (
  getStorage()?.getItem(KIOSK_PIN_VERIFIED_KEY) === "true"
);

export const markKioskPinVerified = () => {
  getStorage()?.setItem(KIOSK_PIN_VERIFIED_KEY, "true");
};

export const verifyKioskPin = (pin) => {
  if (String(pin || "") !== KIOSK_ENTRY_PIN) return false;
  markKioskPinVerified();
  return true;
};

/** Marks the current tab as a kiosk session. Does not sign anyone in. */
export const markKioskSessionActive = () => {
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(KIOSK_SESSION_FLAG_KEY, "true");
  storage.setItem(KIOSK_SESSION_STARTED_AT_KEY, String(Date.now()));
};

/**
 * Clears every trace of the previous kiosk guest: Redux-backed auth storage
 * (token/roles/userData), booking-in-progress state, and the kiosk flag
 * itself. This is the single choke point every kiosk exit path (success,
 * failure, timeout, error, cancel) must run through before returning to
 * /kiosk, so the next guest never sees the previous guest's data.
 */
export const clearKioskSession = async (dispatch) => {
  try {
    if (dispatch) {
      await dispatch(AuthLogout());
    }
  } catch {
    // Even if the server-side logout call fails (offline, expired token),
    // still wipe local state below — a failed network call must never leave
    // the previous guest's session on screen for the next guest.
  }

  const local = getLocalStorage();
  const session = getStorage();

  // localStorage.clear() already runs inside AuthLogout()/logout(), but do it
  // again defensively in case dispatch was unavailable or logout short-circuited.
  local?.clear();
  session?.clear();
};

export const startKioskSession = () => {
  markKioskSessionActive();
};
